import { IntlShape } from 'react-intl';
import { CreditNoteReference } from './CreditNoteStore';
import { Address } from './Address';
import { SaleItemReference } from './SaleItemStore';
import { Intents } from './../npaper/Intents';
import { CompanyReference, PaymentTermsType } from './contacts/CompanyStore';
import {
  types,
  SnapshotIn,
  SnapshotOut,
  Instance,
  getRoot,
  IAnyModelType,
  flow,
} from 'mobx-state-tree';
import { modelWithCRUD } from './utils/modelWithCRUD';
import { ContactReference } from './contacts/ContactStore';
import { RootModelType } from './Root';
import { CommomMixin, ResponsibleMixin } from './Mixins';
import { CostPrice } from './products/ProductStore';
import { VatRateReference } from './VatRateStore';
import { DateTime } from './DateTime';

export enum SaleType {
  OFFER = 'offer',
  ORDER = 'order',
  INVOICE = 'invoice',
}

export enum SaleStatus {
  /**
   * Applies to `SalesType.OFFER`, `SalesType.ORDER` and `SalesType.INVOICE`
   *
   * __Offers__ with status `DRAFT` are newly created and has not been sent to the customer yet.
   * Can transition to _order_ directly or through the `OPEN` status on a send event
   *
   * __Orders__ will have `DRAFT` status on creation and no purchase orders has been created yet.
   * When creating a purchaseorder/choosing how to deliver products, orders transitions to `OPEN`,
   * when sending an order confirmation and sending the purchas eorders.
   *
   * __Invoices__ will have `DRAFT` status on creation (transition from `SalesType.ORDER`).
   * As `DRAFT`, an invoice is waiting for someone to send it to a customer. At this stage, we can
   *   still make changes to the lines - ex. if a purchase-order cannot deliver some items.
   * The invoice will transition to `OPEN` (and be completely locked) once it is sent to the customer.
   */
  DRAFT = 'draft',

  /**
   * Applies to `SalesType.OFFER` and `SalesType.ORDER`
   *
   * __Offers__ with status `OPEN` has (most likely) been sent to the customer, and is waiting confirmation.
   * It can transition to _order_ if it is marked as _won_. It can also transition to _lost_ if a customer
   * rejects the offer completely. In case of a change in the offer (resulting in a new version), one should not mark the offer as lost (though possible), but instead
   * make the changes and send the offer to the customer again - nevertheless, resulting in a reopen event (transtion from open to open).
   *
   * __Orders__ with status `OPEN` has had purchase orders attached to it and (maybe) an order confirmation sent to the customer.
   * An open order can transition to a draft _invoice_ directly, thus making the order "ready for invoicing".
   * The new invoice can still be tracked though ({@link SalesType#DRAFT}).
   */
  OPEN = 'open',

  /**
   * Applies to `SalesType.OFFER`
   *
   * __Offers__ with status `LOST` can be openned by changing and/or sendening the offer again to the customer.
   * The offer can also be deleted. The `LOST` status is used as a transitional stage for working with offers.
   */
  LOST = 'lost',

  /**
   * Applies to `SalesType.OFFER`
   *
   * __Offers__ with status `WON` will immediately transition to a `Order` type with `DRAFT` status.
   */
  WON = 'won',

  /**
   * __Invoiced__ with status `OPEN` has been finalized and sent to the customer.
   * At no point in time after this, can the invoice be changed (though administrative fields like `reference`, `responsible`, `tags`, etc. may be changed).
   * It may transition to `PAID` or `VOIDED`.
   */
  INVOICED = 'invoiced',

  /**
   * Applies to `SalesType.ORDER` and `SalesType.INVOICE`
   *
   * __Orders__ can be voided when `OPEN`, which finalizes the order. Voiding an order will effectively cancel all the lines and cancel any attached purchase order lines.
   *
   * __Invoices__ can be voided from `OPEN` and `DRAFT`, which finalizes the invoice. When an invoice is voided, the lines will be cancelled and any attached purchase order lines will be cancelled.
   */
  VOID = 'void',
}

export enum DeliveryType {
  SHIPPING = 'shipping',
  DROP_SHIPPING = 'dropship',
  PICKUP = 'pickup',
}

export const formatDeliveryType = (intl: IntlShape, type: DeliveryType) => {
  switch (type) {
    case DeliveryType.DROP_SHIPPING:
      return intl.formatMessage({
        defaultMessage: 'Drop shipping',
      });
    case DeliveryType.PICKUP:
      return intl.formatMessage({
        defaultMessage: 'Pick up',
      });
    case DeliveryType.SHIPPING:
      return intl.formatMessage({
        defaultMessage: 'Shipping',
      });
  }
};

export const VatAmountModel = types.model('VatAmount', {
  vat_rate: types.late(() => VatRateReference),
  items: types.array(types.late(() => SaleItemReference)),
  amount: types.number,
});

export const Sale = types
  .compose(
    CommomMixin,
    ResponsibleMixin,
    types.model({
      id: types.union(types.identifier, types.identifierNumber),
      type: types.enumeration<SaleType>('SaleType', Object.values(SaleType)),
      status: types.enumeration<SaleStatus>(
        'SaleStatus',
        Object.values(SaleStatus)
      ),
      language: types.maybeNull(types.string),

      offer_number: types.maybeNull(types.number),
      offer_date: types.maybeNull(DateTime),
      order_number: types.maybeNull(types.number),
      order_date: types.maybeNull(DateTime),
      invoice_number: types.maybeNull(types.number),
      invoice_date: types.maybeNull(DateTime),
      invoice_created_at: types.maybeNull(DateTime),

      payment_terms_type: types.enumeration<PaymentTermsType>(
        'PaymentTermsType',
        Object.values(PaymentTermsType)
      ),
      custom_payment_terms: types.maybeNull(types.string),
      due_date: types.maybeNull(DateTime),
      days_of_credit: types.optional(types.number, 0),

      title: types.string,

      customer: types.late(() => CompanyReference),
      contacts: types.array(types.late(() => ContactReference)),
      invoice_address: types.maybeNull(Address),
      delivery_address: types.maybeNull(Address),
      delivery_type: types.optional(
        types.enumeration<DeliveryType>(
          'DeliveryType',
          Object.values(DeliveryType)
        ),
        DeliveryType.PICKUP
      ),

      currency: types.optional(types.string, 'EUR'),
      sub_total_base: types.number,
      sub_total_base_currency: types.string,
      cost_price_base: types.number,
      cost_price_base_currency: types.string,

      sub_total: types.number, // Computed on API
      total: types.number, // Computed on API
      discount: types.optional(types.number, 0),

      // Vat amounts are not returned on GET /sales/ but must be fetched with GET /sales/{id}/
      vat_amounts: types.array(VatAmountModel),

      cost_price: CostPrice,
      contribution_margin: CostPrice,
      contribution_margin_ratio: CostPrice,

      creditable: types.optional(types.boolean, false),
      credit_notes: types.array(types.late(() => CreditNoteReference)),

      items: types.array(types.late(() => SaleItemReference)),
      needs_purchase_order: types.optional(types.boolean, false),
      // TODO: history: All changes in some nice format. (See django-simple-history)
    })
  )
  .named('Sale')
  .views((self) => ({
    get statusIntent(): Intents {
      return self.status === SaleStatus.DRAFT
        ? 'none'
        : self.status === SaleStatus.OPEN
        ? 'info'
        : self.status === SaleStatus.LOST
        ? 'warning'
        : self.status === SaleStatus.WON
        ? 'success'
        : self.status === SaleStatus.INVOICED
        ? 'success'
        : self.status === SaleStatus.VOID
        ? 'danger'
        : 'none';
    },
    get canDelete(): boolean {
      // We can delete a sale if it is a none draft invoice
      const isNoneDraftInvoice =
        self.type === SaleType.INVOICE && self.status !== SaleStatus.DRAFT;
      return !isNoneDraftInvoice;
    },
  }));

export const SaleStore = modelWithCRUD('sales', Sale)
  .named('SaleStore')
  .actions((self) => ({
    changeState: flow(function* changeState(
      sale: ISale,
      type: SaleType,
      status: SaleStatus
    ) {
      const item = yield self.env.api
        .put(`${self.apiPath}/${sale.id}/change-state/`, {
          type,
          status,
        })
        .then((res) => self.getItem(sale.id, true));
      const root = getRoot(self) as RootModelType;
      self.invalidateCaches();
      root.purchaseOrderStore.invalidateCaches();
      root.saleItemStore.invalidateCaches();
      return item;
    }),
    duplicate: flow(function* duplicate(sale: ISale) {
      const data = yield self.env.api
        .post(`${self.apiPath}/${sale.id}/duplicate/`, {})
        .then((res) => res.data);
      self.itemMap.put(data);
      self.invalidateCaches();
      return self.getItem(data.id)!;
    }),
    procureItems: flow(function* procureItems(
      sale: ISale,
      procurement: { item: string | number; drop_ship: boolean }[]
    ) {
      const { data } = yield self.env.api.post(
        `${self.apiPath}/${sale.id}/procure/`,
        {
          items: procurement,
        }
      );
      yield self.getItem(sale.id, true);
      const root = getRoot(self) as RootModelType;
      self.invalidateCaches();
      root.purchaseOrderStore.invalidateCaches();
      root.saleItemStore.invalidateCaches();
      return self.getItem(data.id)!;
    }),
    sendSale: flow(function* sendSale(
      sale: ISale,
      recipients: string[],
      language: string,
      contacts_cc: boolean,
      responsible_bcc: boolean,
      message: string = '',
      type: SaleType = sale.type
    ) {
      const data = yield self.env.api
        .post(`${self.apiPath}/${sale.id}/send/`, {
          type,
          recipients,
          language,
          message,
          contacts_cc: contacts_cc || false,
          responsible_bcc: responsible_bcc || false,
        })
        .then((res) => res.data);
      yield self.getItem(sale.id, true);
      return data;
    }),
  }));

export interface ISale extends Instance<typeof Sale> {}
export interface ISaleSnapsnotIn extends SnapshotIn<typeof Sale> {}
export interface ISaleSnapsnotOut extends SnapshotOut<typeof Sale> {}

export const SaleReference = types.reference(
  types.late((): IAnyModelType => Sale),
  {
    get(identifier, parent): any {
      return (getRoot(parent!) as RootModelType).saleStore.getItemNaive(
        identifier
      );
    },
    set(value) {
      return value.id;
    },
  }
);
